// Utility function to make a string uppercase
export const toUpperCase = (str) => {
  if (!str) return "";
  return str.toUpperCase();
};

// Utility function to capitalize the first letter of each word in a string
export const capitalizeWords = (str) => {
  if (!str) return "";
  return str
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

// Utility function to capitalize only the first letter of a string
export const capitalizeFirstLetter = (str) => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

// Utility function to convert a string to lowercase
export const toLowerCase = (str) => {
  if (!str) return "";
  return str.toLowerCase();
};

// Utility function to check if a string contains only alphabets
export const isAlpha = (str) => {
  if (!str) return false;
  return /^[A-Za-z]+$/.test(str);
};

// Utility function to truncate a string to a specified length and add "..."
export const truncateString = (str, maxLength) => {
  if (!str || str.length <= maxLength) return str;
  return str.slice(0, maxLength) + "...";
};

// Utility function to reverse a string
export const reverseString = (str) => {
  if (!str) return "";
  return str.split("").reverse().join("");
};

// Utility function to check if a string is a palindrome
export const isPalindrome = (str) => {
  if (!str) return false;
  const sanitized = str.replace(/[^A-Za-z0-9]/g, "").toLowerCase();
  return sanitized === reverseString(sanitized);
};

// Utility function to convert a string to kebab-case
export const toKebabCase = (str) => {
  if (!str) return "";
  return str
    .replace(/([a-z])([A-Z])/g, "$1-$2")
    .replace(/\s+/g, "-")
    .toLowerCase();
};

// Utility function to convert a string to snake_case
export const toSnakeCase = (str) => {
  if (!str) return "";
  return str
    .replace(/([a-z])([A-Z])/g, "$1_$2")
    .replace(/\s+/g, "_")
    .toLowerCase();
};

// Utility function to format date
export const formatDate = (isoDate) => {
  const date = new Date(isoDate);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
};

export const removeUnderScore = (input) => {
  if (input) {
    return input
      .split('_') // Split the string into words by underscore
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1)) // Capitalize the first letter of each word
      .join(' '); // Join the words with a space
  }
};

export function formatURL(inputURL) {
  if (!/^https?:\/\//i.test(inputURL)) {
    return `https://${inputURL}`;
  }
  return inputURL;
}

export const validateURL = (url) => {
  const regex = /^(https?:\/\/)([a-z0-9]+\.)?[a-z0-9]+\.[a-z]{2,6}(\/[\w .-]*)*\/?$/;
  return regex.test(url);
};

export const StingKillInfoOverview = (text) => {
  const urlRegex = /(?:https?|ftp):\/\/[^\s/$.?#].[^\s]*/gi;
  let formattedText = text || "";

  formattedText = formattedText?.replace(urlRegex, (url) => {
    return `<b><a href="${url}" target="_blank">${url}</a></b>`;
  });

  formattedText = formattedText
    // Remove "[" and "]" characters
    .replace(/\[([^\]]*?)\]/g, "$1")

    // Bold formatting for **text**
    .replace(/\*\*(.+?)\*\*/g, "<b>$1</b>")

    // Headings: ### Text
    .replace(/### (.*?)(?=\n|$)/g, '<h3 style="color: #3a96b8;">$1</h3>')

    // Replace ', with <br/> (only for exact pattern)
    .replace(/',/g, "<br/>")

    // Replace line breaks with <br/> tag, ensuring not to break headings
    .replace(/(?<!<\/h3>)\n/g, "<br/>")

    // Optional: Sanitize HTML to prevent injection
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/&lt;(\/?[a-z]+.*?)&gt;/g, "<$1>");

  return <p dangerouslySetInnerHTML={{ __html: formattedText }} />;
};

export const StingKillInfoOverviewForUpdate = (text) => {
  const urlRegex = /(?:https?|ftp):\/\/[^\s/$.?#].[^\s]*/gi;
  let formattedText = text || "";

  formattedText = formattedText?.replace(urlRegex, (url) => {
    return `<b><a href="${url}" target="_blank">${url}</a></b>`;
  });

  formattedText = formattedText
    // Remove "[" and "]" characters
    .replace(/\[([^\]]*?)\]/g, "$1")

    // Bold formatting for **text**
    .replace(/\*\*(.+?)\*\*/g, "<b>$1</b>")

    // Headings: ### Text
    .replace(/### (.*?)(?=\n|$)/g, '<h3 style="color: #3a96b8;">$1</h3>')

    // Replace ', with <br/> (only for exact pattern)
    .replace(/',/g, "<br/>")

    // Replace line breaks with <br/> tag, ensuring not to break headings
    .replace(/(?<!<\/h3>)\n\n/g, "<br/>")

    // Optional: Sanitize HTML to prevent injection
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/&lt;(\/?[a-z]+.*?)&gt;/g, "<$1>");

  return <p dangerouslySetInnerHTML={{ __html: formattedText }} />;
};

export const formatParagraph = (text) => {
  if (!text) return "";

  // Helper function to check if a string contains a time format
  const containsTimeFormat = (str) => {
    const timePatterns = [
      /\d{1,2}:\d{2}\s*(?:AM|PM|am|pm)?/, // 8:00 AM, 08:00, 8:00pm
      /\d{1,2}(?::\d{2})?\s*(?:AM|PM|am|pm)/, // 8 AM, 8PM, 8:00 PM
      /\d{4}\s*(?:hrs|HRS|hours)/, // 1400hrs, 2300 HRS
      /\d{1,2}[:.]\d{2}/, // 24-hour format or decimal time: 14:00, 14.00
    ];
    return timePatterns.some((pattern) => pattern.test(str));
  };

  // Helper function to check if a line should be treated as a header
  const isHeader = (line) => {
    // Skip if line is empty or already bold
    if (!line || line.startsWith("**")) return false;

    // Skip if line contains time format
    if (containsTimeFormat(line)) return false;

    // Common non-header phrases that contain colons
    const nonHeaderPhrases = [
      /note:/i,
      /attention:/i,
      /tel:/i,
      /phone:/i,
      /email:/i,
      /e-mail:/i,
      /fax:/i,
      /ref:/i,
      /reference:/i,
      /source:/i,
      /ratio:/i,
      /price:/i,
      /cost:/i,
      /(https?|ftp):\/\//, // URLs
      /[a-zA-Z]:\\/, // Windows file paths
      /\w+:\w+/, // No spaces around colon (like "Category:Item")
    ];

    // Return false if line matches any non-header pattern
    if (nonHeaderPhrases.some((pattern) => pattern.test(line))) return false;

    // Check if line ends with a colon and has valid header format
    return /^[A-Za-z][\w\s,&-]+:/.test(line);
  };

  // Process text line by line
  let lines = text.split("\n");
  let formattedLines = lines.map((line) => {
    line = line.trim();

    // Handle bullet points
    if (line.startsWith("-")) {
      return `- ${line.substring(1).trim()}`;
    }

    // Handle headers
    if (isHeader(line)) {
      const colonIndex = line.indexOf(":");
      const headerText = line.substring(0, colonIndex).trim();
      const remainingText = line.substring(colonIndex + 1).trim();
      return `**${headerText}**:${remainingText ? "\n" + remainingText : ""}`;
    }

    return line;
  });

  // Join lines and clean up formatting
  let formattedText = formattedLines.join("\n");

  // Handle special sections that need extra spacing
  const specialSections = [
    "Additionally:",
    "Nearby attractions:",
    "Furthermore:",
    "Moreover:",
    "In addition:",
    "Note:",
    "Important:",
  ];

  specialSections.forEach((section) => {
    formattedText = formattedText.replace(
      new RegExp(`(\\n|^)${section}`, "g"),
      `\n\n${section}`
    );
  });

  // Clean up multiple consecutive line breaks
  formattedText = formattedText.replace(/\n{3,}/g, "\n\n").trim();

  return formattedText;
};

export const StingKillInfo = (text) => {
  const urlRegex = /(?:https?|ftp):\/\/[^\s/$.?#].[^\s]*/gi;
  let formattedText = text || "";

  formattedText = formattedText?.replace(urlRegex, (url) => {
    return `<b><a href="${url}" target="_blank">${url}</a></b>`;
  });

  formattedText = formattedText
    .replace(/\*\*(.*?)\*\*/g, "<b>$1</b>")
    .replace(/### (.*?)(?=\n|$)/g, '<h3 style="color: #e55426;">$1</h3>')
    .replace(/\n/g, "<br/>");

  return <p dangerouslySetInnerHTML={{ __html: formattedText }} />;
};

